// ============================================================================
// OPS MANAGER DASHBOARD VIEW — Single-page operational control panel
//
// Three layers:
//   TOP    — Live Fires: stuck referrals by stage with counts + thresholds
//   CENTER — Referral Funnel: full pipeline flow + diverted/destroyed counts
//   BOTTOM — System Quality + Value: Gate quality, rework reality, capacity
//
// Data sources (all zero-PHI, zero collection scans):
//   - tenants/{tenantId}/referrals (active items via listOpsActiveItems)
//   - tenants/{tenantId}/settings/impact (SLA + business hours config)
//   - tenants/{tenantId}/rollups/daily/dates/{YYYY-MM-DD} (daily rollups)
//
// Live Fires uses tenant Impact Settings for SLA thresholds:
//   - Business-hours-aware elapsed time for UNCLAIMED_NEW, NEEDS_INFO, SEEN
//   - READY_TO_SCHEDULE split into Triage (untriaged) and Scheduling (triaged)
//   - Triage card: 3 sub-clocks (urgent/routine/hold), nuclear override on urgent breach
//   - Scheduling card: clock starts at triageCompletedAt, business hours
//   - Calendar duration (appointmentDate − scheduledAt) for SCHEDULED
//   - Warn/Critical based on % of queue stuck (not raw counts)
//
// Access: ops_manager, tenant_admin, platform_admin (SECTOR_ANALYTICS)
// Route: 'ops-dashboard'
// Zero-PHI invariant: this view never reads from /phi/**.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { safeUserError } from '../../core/safeError.js';
import {
  AlertTriangle, Activity, RefreshCw, RotateCcw, Info,
  TrendingUp, Shield, FileText, ClipboardList, CheckCircle2,
  Clock, Flame, ArrowRight, ArrowDown, ChevronDown, ChevronUp,
  Zap, Timer, Ban, BarChart3, Calendar, Eye, Megaphone,
} from 'lucide-react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import CreateQueuePingModal from '../CreateQueuePingModal.jsx';
import { QUEUE_PING_PURPOSE } from '../../core/index.js';
import {
  getDailyRollups,
  getCohortRollups,
  getNotYetCohortRollups,
  buildDateRange,
  getFlowQualityTotals,
  getImpactSettings,
  listOpsActiveItems,
  getSpecialties,
  DEFAULT_IMPACT_SETTINGS,
  WORKFLOW_STATUS,
  sumField,
  businessHoursElapsed,
  queueSlaStatus,
  classifyTriageOutcome,
} from '../../core/index.js';
import { getOpsSnapshot } from '../../core/analyticsApi.js';

// ============================================================================
// HELPERS
// ============================================================================

const pct = (num, den) => {
  if (!den || den === 0) return null;
  return Math.round((num / den) * 100);
};

const fmtPct = (val) => (val === null ? '—' : `${val}%`);
const fmtN = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

// Guided-learning focus highlight — matches the app convention used by OpsHomeView:
// ring + pulse when this element's learning target is the active guided step.
// Returns a leading-space class string (or '') so it can be appended to className.
const focusHighlight = (target, activeFocusTarget) =>
  (target && activeFocusTarget === target ? ' ring-2 ring-cyan-400 animate-pulse rounded-xl' : '');

// ── Rollup freshness (B6) ──────────────────────────────────────────────────
const FRESH_RECENT_MS = 60 * 1000;            // < 60s = "recently updated"
const FRESH_STALE_MS  = 26 * 60 * 60 * 1000;  // >= 26h = "may be stale"

function fmtRelativeAge(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function extractSpecialtyBreakdown(rollup) {
  const result = {};
  if (rollup.breakdowns?.bySpecialty) return rollup.breakdowns.bySpecialty;
  const prefix = 'breakdowns.bySpecialty.';
  Object.keys(rollup).forEach((key) => {
    if (key.startsWith(prefix)) {
      const rest = key.substring(prefix.length);
      const dotIdx = rest.indexOf('.');
      if (dotIdx === -1) return;
      const specId = rest.substring(0, dotIdx);
      const fieldPath = rest.substring(dotIdx + 1);
      if (!result[specId]) result[specId] = {};
      const parts = fieldPath.split('.');
      let node = result[specId];
      for (let i = 0; i < parts.length - 1; i++) {
        if (!node[parts[i]]) node[parts[i]] = {};
        node = node[parts[i]];
      }
      node[parts[parts.length - 1]] = rollup[key] || 0;
    }
  });
  return result;
}

function safeSpecNum(specData, ...path) {
  let node = specData;
  for (const p of path) {
    if (node == null || typeof node !== 'object') return null;
    node = node[p];
  }
  return typeof node === 'number' ? node : null;
}

function getSpecialtyFilteredTotals(rollups, specialtyId) {
  const totals = {
    gateSubmissions: 0, routine: 0, urgent: 0, not_yet: 0,
    referralsWritten: 0, created: 0, completed: 0, overrides: 0,
    reclaimedMinutes: 0,
    flowCreated: 0, flowNeedsInfo: 0, flowReadyToSchedule: 0, flowScheduled: 0,
    flowSeen: 0, flowClosedLoop: 0,
    reworkCreated: 0, reworkTouches: 0, reworkResolved: 0,
    hasGateData: false, hasWorkflowData: false, hasReworkData: false,
  };

  for (const rollup of rollups) {
    const breakdown = extractSpecialtyBreakdown(rollup);
    const sd = breakdown[specialtyId];
    if (!sd) continue;

    const gt = safeSpecNum(sd, 'gate', 'totalRuns');
    if (gt !== null) {
      totals.hasGateData = true;
      totals.gateSubmissions += gt;
      totals.routine += safeSpecNum(sd, 'gate', 'routine') ?? 0;
      totals.urgent += safeSpecNum(sd, 'gate', 'urgent') ?? 0;
      totals.not_yet += safeSpecNum(sd, 'gate', 'not_yet') ?? 0;
      totals.completed += safeSpecNum(sd, 'gate', 'completed') ?? 0;
      totals.overrides += safeSpecNum(sd, 'gate', 'overrides') ?? 0;
      totals.created += safeSpecNum(sd, 'gate', 'created') ?? 0;
    }

    const cm = safeSpecNum(sd, 'capacity', 'reclaimedMinutes');
    if (cm !== null) totals.reclaimedMinutes += cm;

    const wfCreated = safeSpecNum(sd, 'workflow', 'flow.created');
    const wfNi = safeSpecNum(sd, 'workflow', 'flow.needsInfo');
    if (wfCreated !== null || wfNi !== null) {
      totals.hasWorkflowData = true;
      totals.flowCreated += wfCreated ?? 0;
      totals.flowNeedsInfo += wfNi ?? 0;
      totals.flowReadyToSchedule += safeSpecNum(sd, 'workflow', 'flow.readyToSchedule') ?? 0;
      totals.flowScheduled += safeSpecNum(sd, 'workflow', 'flow.scheduled') ?? 0;
      totals.flowSeen += safeSpecNum(sd, 'workflow', 'flow.seen') ?? 0;
      totals.flowClosedLoop += safeSpecNum(sd, 'workflow', 'flow.closedLoop') ?? 0;
    }

    const rc = safeSpecNum(sd, 'rework', 'requestsCreated');
    if (rc !== null) {
      totals.hasReworkData = true;
      totals.reworkCreated += rc;
      totals.reworkTouches += safeSpecNum(sd, 'rework', 'touchesLogged') ?? 0;
      totals.reworkResolved += safeSpecNum(sd, 'rework', 'resolved') ?? 0;
    }
  }

  totals.referralsWritten = totals.routine + totals.urgent;
  return totals;
}

const fmtDuration = (totalMinutes) => {
  const m = Math.round(totalMinutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem} min`;
};

// ============================================================================
// LIVE FIRES — business-hours-aware stuck detection from tenant Impact Settings
// ============================================================================

function toMillis(ts) {
  if (!ts) return null;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

// businessHoursElapsed is imported from core (single source of truth).

function isScheduledStuck(item, slaHours) {
  const apptStr = item.workflow?.scheduling?.appointmentDate;
  // Anchor on the actual scheduled time; fall back to status entry only if absent.
  const scheduledMs = toMillis(item.workflow?.scheduling?.scheduledAt)
                   || toMillis(item.workflow?.statusEnteredAt);
  if (!apptStr || !scheduledMs) return false;
  // Parse the calendar date deterministically (UTC) so the result is independent
  // of the viewer's browser timezone.
  const apptMs = new Date(apptStr + 'T00:00:00Z').getTime();
  if (Number.isNaN(apptMs)) return false;
  return (apptMs - scheduledMs) / (60 * 60 * 1000) >= slaHours;
}

function isStatusStuck(item, nowMs, slaHours, bh) {
  const enteredMs = toMillis(item.workflow?.statusEnteredAt)
                 || toMillis(item.workflow?.statusUpdatedAt)
                 || toMillis(item.workflow?.createdAt);
  if (!enteredMs) return false;
  return businessHoursElapsed(enteredMs, nowMs, bh) >= slaHours;
}

const FIRE_COLORS = {
  ok:       { bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', text: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-300' },
  warn:     { bg: 'bg-amber-500/10',   border: 'border-amber-500/25',   text: 'text-amber-400',   badge: 'bg-amber-500/20 text-amber-300' },
  critical: { bg: 'bg-red-500/10',     border: 'border-red-500/25',     text: 'text-red-400',     badge: 'bg-red-500/20 text-red-300' },
};

const LiveFireCard = ({ label, mainCopy, helperText, stuckCount, queueCount, warnThreshold, critThreshold, stuckPct, status, icon: Icon, emptyText, learningTarget, activeFocusTarget, onSendPing }) => {
  const c = FIRE_COLORS[status] || FIRE_COLORS.ok;
  const isAlert = status !== 'ok';
  const focusCls = focusHighlight(learningTarget, activeFocusTarget);

  return (
    <div data-learning-target={learningTarget} className={`rounded-2xl border p-5 ${c.bg} ${c.border} backdrop-blur-sm flex flex-col gap-3${focusCls}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
        <div className={`w-7 h-7 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.text}`} />
        </div>
      </div>
      {queueCount === 0 ? (
        <div className="text-sm text-slate-500">{emptyText || 'No active referrals at this stage'}</div>
      ) : (
        <>
          <div className={`text-3xl font-bold ${c.text} leading-none`}>{fmtN(stuckCount)}</div>
          <div className={`text-xs ${isAlert ? c.text : 'text-slate-500'}`}>
            {mainCopy || `${stuckPct}% breaching SLA`}
          </div>
        </>
      )}
      <div className="flex items-center gap-2">
        {status === 'critical' && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>
            <Flame className="w-3 h-3 inline mr-1" />Critical — {stuckPct}% breaching SLA
          </span>
        )}
        {status === 'warn' && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>
            <AlertTriangle className="w-3 h-3 inline mr-1" />WARNING
          </span>
        )}
        {status === 'ok' && queueCount > 0 && (
          <span className="text-xs text-slate-500">Within threshold</span>
        )}
      </div>
      {queueCount > 0 && (
        <div className="text-xs text-slate-600">
          Warn: {warnThreshold}% | Critical: {critThreshold}%
        </div>
      )}
      <div className="text-[10px] text-slate-600">{helperText}</div>
      {onSendPing && isAlert && queueCount > 0 && (
        <button
          onClick={onSendPing}
          className="mt-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 text-[11px] font-medium transition-all self-start"
        >
          <Megaphone className="w-3.5 h-3.5" /> Send Queue Ping
        </button>
      )}
    </div>
  );
};

// ============================================================================
// FUNNEL STAGE CARD
// ============================================================================

const FunnelBox = ({ label, count, color, icon: Icon, subtitle, tooltip, learningTarget, activeFocusTarget }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-learning-target={learningTarget} className={`
      flex-1 min-w-0 rounded-2xl border p-4 flex flex-col gap-2
      bg-slate-900/60 backdrop-blur-sm border-${color}-500/25${focusHighlight(learningTarget, activeFocusTarget)}
    `}>
      <div className="flex items-center justify-between">
        <Icon className={`w-4 h-4 text-${color}-400`} />
        <div className="flex items-center gap-1.5">
          {tooltip && (
            <button
              onClick={() => setOpen((p) => !p)}
              className="text-slate-600 hover:text-slate-400 transition-colors"
              title="Show details"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          )}
          <span className={`text-2xl font-bold text-${color}-300 leading-none`}>{count == null ? '—' : fmtN(count)}</span>
        </div>
      </div>
      <div className="text-xs text-slate-400 leading-tight">{label}</div>
      {subtitle && <div className="text-xs text-slate-600 leading-tight">{subtitle}</div>}
      {open && tooltip && (
        <div className="mt-1 p-2 rounded-lg bg-slate-800/60 text-[11px] text-slate-400 leading-relaxed">
          {tooltip}
        </div>
      )}
    </div>
  );
};

const FunnelArrow = ({ dimmed = false }) => (
  <div className="flex-shrink-0 flex items-center px-0.5">
    <ArrowRight className={`w-4 h-4 ${dimmed ? 'text-slate-700' : 'text-slate-600'}`} />
  </div>
);

// ============================================================================
// DIVERTED / DESTROYED ROW
// ============================================================================

const DivertedCard = ({ label, count, display, color, icon: Icon, tooltip, learningTarget, activeFocusTarget }) => {
  const [open, setOpen] = useState(false);

  return (
    <div data-learning-target={learningTarget} className={`
      rounded-xl border p-3
      bg-slate-900/40 backdrop-blur-sm border-${color}-500/20${focusHighlight(learningTarget, activeFocusTarget)}
    `}>
      <div className="flex items-center gap-3">
        <div className={`w-6 h-6 rounded-lg bg-${color}-500/15 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-3.5 h-3.5 text-${color}-400`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-lg font-bold text-${color}-300 leading-none`}>{display ?? fmtN(count)}</div>
          <div className="text-xs text-slate-500 leading-tight mt-0.5">{label}</div>
        </div>
        {tooltip && (
          <button
            onClick={() => setOpen((p) => !p)}
            className="text-slate-600 hover:text-slate-400 transition-colors flex-shrink-0"
            title="Show details"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && tooltip && (
        <div className="mt-2 p-2 rounded-lg bg-slate-800/60 text-xs text-slate-400 leading-relaxed">
          {tooltip}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// KPI METRIC CARD
// ============================================================================

const KpiCard = ({ label, value, unit, color, formula, icon: Icon, learningTarget, activeFocusTarget }) => {
  const [showFormula, setShowFormula] = useState(false);

  return (
    <div data-learning-target={learningTarget} className={`rounded-2xl border p-5 bg-slate-900/60 backdrop-blur-sm border-${color}-500/25${focusHighlight(learningTarget, activeFocusTarget)}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 text-${color}-400`} />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider leading-tight">{label}</span>
        </div>
        {formula && (
          <button
            onClick={() => setShowFormula((p) => !p)}
            className="text-slate-600 hover:text-slate-400 transition-colors"
            title="Show formula"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className={`text-2xl font-bold text-${color}-300 leading-none`}>
        {value}{unit && <span className="text-base font-normal text-slate-500 ml-1">{unit}</span>}
      </div>
      {showFormula && formula && (
        <div className="mt-3 p-2.5 rounded-lg bg-slate-800/60 text-xs text-slate-400 leading-relaxed">
          {formula}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SECTION HEADER
// ============================================================================

const SectionHeader = ({ icon: Icon, title, subtitle, color = 'indigo' }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={`w-9 h-9 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
      <Icon className={`w-5 h-5 text-${color}-400`} />
    </div>
    <div>
      <h2 className="text-base font-semibold text-white">{title}</h2>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
  </div>
);

// ============================================================================
// PERIOD SELECTOR
// ============================================================================

const PeriodBtn = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`
      px-4 py-1.5 rounded-xl text-sm font-medium transition-all
      ${active
        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30'
        : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60'
      }
    `}
  >
    {label}
  </button>
);

// ============================================================================
// MAIN VIEW
// ============================================================================

export default function OpsManagerDashboardView({ tenantIdOverride, hideLiveFires, headerSlot, allTenantIds, activeFocusTarget = null }) {
  const { user } = useContext(AuthContext);
  const tenantId = tenantIdOverride ?? user?.tenantId ?? null;
  const isMultiTenant = Array.isArray(allTenantIds) && allTenantIds.length > 0;

  // Queue Ping creation — ops_manager / tenant_admin only, from an active fire.
  // Not available in multi-tenant ("All Tenants") view (no single tenant context).
  const canSendQueuePing = !isMultiTenant && (user?.role === 'ops_manager' || user?.role === 'tenant_admin');
  const [queuePingPurpose, setQueuePingPurpose] = useState(null);
  const openSendPing = (purpose) => () => setQueuePingPurpose(purpose);

  // Guided learning: scroll the active focus target into view when it changes.
  // Highlight/pulse styling is applied via className (focusHighlight) on each
  // target element/card. Missing targets (hidden section, no data, '—' state)
  // resolve to null here and are a safe no-op — no crash, no console error.
  useEffect(() => {
    if (!activeFocusTarget || typeof document === 'undefined') return;
    const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, [activeFocusTarget]);

  const [period,      setPeriod]      = useState(30);
  const [rollups,     setRollups]     = useState([]);
  const [snapshot,    setSnapshot]    = useState(null);
  const [impact,      setImpact]      = useState(null);
  const [liveItems,   setLiveItems]   = useState(null);
  const [specialties, setSpecialties] = useState({});
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Entered-ops cohort rollups (tenant-level only). null = unavailable
  // (multi-tenant view or load not yet complete).
  const [cohort,      setCohort]      = useState(null);
  // Not-Yet recovery cohort rollups (tenant-level only). null = unavailable.
  const [notYetCohort, setNotYetCohort] = useState(null);

  const load = useCallback(async () => {
    if (!isMultiTenant && !tenantId) {
      setError('No tenant context. Cannot load dashboard.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const dateIds = buildDateRange(period);
      if (isMultiTenant) {
        const perTenant = await Promise.all(
          allTenantIds.map(async (tid) => {
            const [docs, specMap] = await Promise.all([
              getDailyRollups(tid, dateIds).catch(() => []),
              getSpecialties(tid).catch(() => ({})),
            ]);
            return { docs, specMap };
          }),
        );
        const allRollups = perTenant.flatMap((r) => r.docs);
        const mergedSpecs = Object.assign({}, ...perTenant.map((r) => r.specMap));
        setRollups(allRollups);
        setSnapshot(null);
        setImpact(null);
        setLiveItems([]);
        setSpecialties(mergedSpecs);
        setCohort(null); // cohort rollups are per-tenant; unavailable in aggregate view
        setNotYetCohort(null);
      } else {
        const [docs, snap, impactDoc, activeItems, specMap, cohortTotals, notYetTotals] = await Promise.all([
          getDailyRollups(tenantId, dateIds),
          getOpsSnapshot(tenantId).catch(() => null),
          getImpactSettings(tenantId).catch(() => null),
          hideLiveFires ? Promise.resolve([]) : listOpsActiveItems(tenantId).catch((err) => { console.error('[LiveFires] listOpsActiveItems failed:', err); return []; }),
          getSpecialties(tenantId).catch(() => ({})),
          getCohortRollups(tenantId, dateIds).catch((err) => { console.error('[Cohort] getCohortRollups failed:', err); return null; }),
          getNotYetCohortRollups(tenantId, dateIds).catch((err) => { console.error('[Cohort] getNotYetCohortRollups failed:', err); return null; }),
        ]);
        setRollups(docs);
        setSnapshot(snap);
        setImpact(impactDoc);
        setLiveItems(activeItems);
        setSpecialties(specMap);
        setCohort(cohortTotals);
        setNotYetCohort(notYetTotals);
      }
      setLastRefresh(new Date());
    } catch (err) {
      setError(safeUserError(err) ?? 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, period, isMultiTenant, allTenantIds, hideLiveFires]);

  useEffect(() => { load(); }, [load]);

  // ── Computed metrics ────────────────────────────────────────────────────

  const isFiltered = selectedSpecialtyId !== null;

  const totals = useMemo(() => {
    if (!isFiltered) return getFlowQualityTotals(rollups);
    const st = getSpecialtyFilteredTotals(rollups, selectedSpecialtyId);
    return {
      gateSubmissions: st.gateSubmissions,
      referralsWritten: st.referralsWritten,
      routineClean: st.routine,
      firstPassAccepted: st.referralsWritten,
      enteredOps: st.hasWorkflowData ? st.flowNeedsInfo + st.flowReadyToSchedule + st.flowScheduled + st.flowSeen + st.flowClosedLoop : null,
      reachedClosedLoop: st.hasWorkflowData ? st.flowClosedLoop : null,
      flowCreated: st.hasWorkflowData ? st.flowCreated : null,
      flowNeedsInfo: st.hasWorkflowData ? st.flowNeedsInfo : null,
      flowReadyToSchedule: st.hasWorkflowData ? st.flowReadyToSchedule : null,
      flowScheduled: st.hasWorkflowData ? st.flowScheduled : null,
      flowSeen: st.hasWorkflowData ? st.flowSeen : null,
      flowClosedLoop: st.hasWorkflowData ? st.flowClosedLoop : null,
      reworkCreated: st.hasReworkData ? st.reworkCreated : null,
      reworkTouches: st.hasReworkData ? st.reworkTouches : null,
      reworkResolved: st.hasReworkData ? st.reworkResolved : null,
      reclaimedMinutes: st.reclaimedMinutes,
    };
  }, [rollups, isFiltered, selectedSpecialtyId]);

  const liveFireCards = useMemo(() => {
    const allItems = liveItems ?? [];
    const items = isFiltered
      ? allItems.filter(it => it.referral?.specialtyId === selectedSpecialtyId)
      : allItems;
    const settings = impact ?? DEFAULT_IMPACT_SETTINGS;
    const sla = settings.sla ?? DEFAULT_IMPACT_SETTINGS.sla;
    const bh = settings.businessHours ?? DEFAULT_IMPACT_SETTINGS.businessHours;
    const states = sla.states ?? DEFAULT_IMPACT_SETTINGS.sla.states;
    const triageSla = sla.triage ?? DEFAULT_IMPACT_SETTINGS.sla.triage;
    const schedulingSla = sla.scheduling ?? DEFAULT_IMPACT_SETTINGS.sla.scheduling;
    const thresholds = sla.thresholds ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds;
    const warnPct = thresholds.warnPercent ?? 0.10;
    const critPct = thresholds.criticalPercent ?? 0.25;
    const nowMs = Date.now();

    const buckets = {
      UNCLAIMED_NEW: { items: [], slaHours: states.UNCLAIMED_NEW?.slaHours ?? 4 },
      NEEDS_INFO:    { items: [], slaHours: states.NEEDS_INFO?.slaHours ?? 48 },
      SCHEDULED:     { items: [], slaHours: states.SCHEDULED?.slaHours ?? 168 },
      SEEN:          { items: [], slaHours: states.SEEN?.slaHours ?? 72 },
    };

    const triageItems = [];
    const schedulingItems = [];

    for (const item of items) {
      const status = item.workflow?.status;
      if (status === WORKFLOW_STATUS.NEW) {
        const hasOwner = item.workflow?.assignedTo || item.workflow?.ownerUid;
        if (!hasOwner) {
          buckets.UNCLAIMED_NEW.items.push(item);
        }
      } else if (status === WORKFLOW_STATUS.NEEDS_INFO) {
        buckets.NEEDS_INFO.items.push(item);
      } else if (status === WORKFLOW_STATUS.READY_TO_SCHEDULE) {
        const w = item.workflow;
        const triaged = !!(w?.triage?.triageCompletedAt || w?.triageCompletedAt);
        // On-hold items go to triage (caught by the hold sub-clock) regardless of
        // triage completion — never dropped. Triaged + not held → scheduling.
        // Untriaged + not held → triage (urgent/routine sub-clocks).
        if (w?.onHold === true) {
          triageItems.push(item);
        } else if (triaged) {
          schedulingItems.push(item);
        } else {
          triageItems.push(item);
        }
      } else if (status === WORKFLOW_STATUS.SCHEDULED) {
        buckets.SCHEDULED.items.push(item);
      } else if (status === WORKFLOW_STATUS.SEEN) {
        buckets.SEEN.items.push(item);
      }
    }

    function buildCard(key) {
      const bucket = buckets[key];
      const queueCount = bucket.items.length;
      let stuckCount = 0;

      if (key === 'SCHEDULED') {
        stuckCount = bucket.items.filter(it => isScheduledStuck(it, bucket.slaHours)).length;
      } else {
        stuckCount = bucket.items.filter(it => isStatusStuck(it, nowMs, bucket.slaHours, bh)).length;
      }

      const stuckPct = queueCount > 0 ? Math.round((stuckCount / queueCount) * 100) : 0;
      const status = queueSlaStatus(stuckCount, queueCount, warnPct, critPct);

      return { stuckCount, queueCount, stuckPct, status, warnThreshold: Math.round(warnPct * 100), critThreshold: Math.round(critPct * 100) };
    }

    // Triage card — separate SLA clocks per sub-category
    const triageUrgentSla  = triageSla.urgentBusinessHours ?? 4;
    const triageRoutineSla = triageSla.routineBusinessHours ?? 48;
    const triageHoldSla    = triageSla.clinicalHoldBusinessHours ?? 72;

    let triageUrgentStuck = 0, triageRoutineStuck = 0, triageHoldStuck = 0;
    let triageUrgentCount = 0, triageRoutineCount = 0, triageHoldCount = 0;

    for (const item of triageItems) {
      const outcome = classifyTriageOutcome(item);
      const isOnHold = item.workflow?.onHold === true;

      if (isOnHold) {
        triageHoldCount++;
        const holdStartMs = toMillis(item.workflow?.onHoldStartedAt);
        if (holdStartMs && businessHoursElapsed(holdStartMs, nowMs, bh) >= triageHoldSla) {
          triageHoldStuck++;
        }
      } else if (outcome === 'URGENT') {
        triageUrgentCount++;
        const enteredMs = toMillis(item.workflow?.statusEnteredAt) || toMillis(item.workflow?.statusUpdatedAt);
        if (enteredMs && businessHoursElapsed(enteredMs, nowMs, bh) >= triageUrgentSla) {
          triageUrgentStuck++;
        }
      } else if (outcome === 'ROUTINE') {
        triageRoutineCount++;
        const enteredMs = toMillis(item.workflow?.statusEnteredAt) || toMillis(item.workflow?.statusUpdatedAt);
        if (enteredMs && businessHoursElapsed(enteredMs, nowMs, bh) >= triageRoutineSla) {
          triageRoutineStuck++;
        }
      }
    }

    const triageTotalStuck = triageUrgentStuck + triageRoutineStuck + triageHoldStuck;
    const triageQueueCount = triageItems.length;
    const triageStuckPct = triageQueueCount > 0 ? Math.round((triageTotalStuck / triageQueueCount) * 100) : 0;

    // Nuclear override: any urgent SLA breach forces Critical
    let triageStatus;
    if (triageUrgentStuck > 0) {
      triageStatus = 'critical';
    } else {
      triageStatus = queueSlaStatus(triageTotalStuck, triageQueueCount, warnPct, critPct);
    }

    const triageCard = {
      stuckCount: triageTotalStuck,
      queueCount: triageQueueCount,
      stuckPct:   triageStuckPct,
      status:     triageStatus,
      warnThreshold: Math.round(warnPct * 100),
      critThreshold: Math.round(critPct * 100),
      urgentStuck:  triageUrgentStuck,
      urgentCount:  triageUrgentCount,
      urgentSlaHours: triageUrgentSla,
      urgentStatus: triageUrgentStuck > 0 ? 'critical' : queueSlaStatus(triageUrgentStuck, triageUrgentCount, warnPct, critPct),
      routineStuck: triageRoutineStuck,
      routineCount: triageRoutineCount,
      routineSlaHours: triageRoutineSla,
      routineStatus: queueSlaStatus(triageRoutineStuck, triageRoutineCount, warnPct, critPct),
      holdStuck:    triageHoldStuck,
      holdCount:    triageHoldCount,
      holdSlaHours: triageHoldSla,
      holdStatus:   queueSlaStatus(triageHoldStuck, triageHoldCount, warnPct, critPct),
      nuclearOverride: triageUrgentStuck > 0,
    };

    // Scheduling card — uses triageCompletedAt as clock, raw ms formula to match SchedulerHomeView
    const schedulingSlaHours = schedulingSla.businessHours ?? 48;
    const schedulingSlaMs = schedulingSlaHours * 60 * 60 * 1000;
    let schedulingStuck = 0;
    for (const item of schedulingItems) {
      const clockMs = toMillis(item.workflow?.triageCompletedAt)
                   || toMillis(item.workflow?.triage?.triageCompletedAt);
      if (clockMs && (nowMs - clockMs) >= schedulingSlaMs) {
        schedulingStuck++;
      }
    }
    const schedulingQueueCount = schedulingItems.length;
    const schedulingStuckPct = schedulingQueueCount > 0 ? Math.round((schedulingStuck / schedulingQueueCount) * 100) : 0;
    const schedulingStatus = queueSlaStatus(schedulingStuck, schedulingQueueCount, warnPct, critPct);

    const schedulingCard = {
      stuckCount: schedulingStuck,
      queueCount: schedulingQueueCount,
      stuckPct:   schedulingStuckPct,
      status:     schedulingStatus,
      warnThreshold: Math.round(warnPct * 100),
      critThreshold: Math.round(critPct * 100),
    };

    // Unreconciled Visits — SCHEDULED appointments whose date has already passed
    // but whose visit outcome (Seen / No-Show / Cancelled) was never recorded.
    // Reuses the Specialist/Specialist-Admin definition (SpecialistQueueHealthView):
    // status === SCHEDULED AND appointmentDate is before today. Distinct from
    // Long-Dated Appointments (far-FUTURE scheduled) and Seen, Not Closed (already SEEN).
    // Date strings are YYYY-MM-DD (UTC), so lexicographic comparison is valid.
    const todayStr = (() => {
      const d = new Date();
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    })();
    const scheduledItems = buckets.SCHEDULED.items;
    const unreconciledStuck = scheduledItems.filter((it) => {
      const ds = it.workflow?.scheduling?.appointmentDate;
      return typeof ds === 'string' && ds < todayStr;
    }).length;
    const unreconciledQueue = scheduledItems.length;
    const unreconciledCard = {
      stuckCount: unreconciledStuck,
      queueCount: unreconciledQueue,
      stuckPct:   unreconciledQueue > 0 ? Math.round((unreconciledStuck / unreconciledQueue) * 100) : 0,
      status:     queueSlaStatus(unreconciledStuck, unreconciledQueue, warnPct, critPct),
      warnThreshold: Math.round(warnPct * 100),
      critThreshold: Math.round(critPct * 100),
    };

    return {
      unclaimedNew: buildCard('UNCLAIMED_NEW'),
      needsInfo:    buildCard('NEEDS_INFO'),
      triage:       triageCard,
      scheduling:   schedulingCard,
      scheduled:    buildCard('SCHEDULED'),
      unreconciled: unreconciledCard,
      seen:         buildCard('SEEN'),
    };
  }, [liveItems, impact, isFiltered, selectedSpecialtyId]);

  const totalStuck = (liveFireCards.unclaimedNew.stuckCount + liveFireCards.needsInfo.stuckCount
                   + liveFireCards.triage.stuckCount + liveFireCards.scheduling.stuckCount
                   + liveFireCards.scheduled.stuckCount + liveFireCards.unreconciled.stuckCount
                   + liveFireCards.seen.stuckCount);

  // Diverted / destroyed flow
  const notYetCount = useMemo(() => {
    if (isFiltered) {
      const specTotals = getSpecialtyFilteredTotals(rollups, selectedSpecialtyId);
      return specTotals.not_yet;
    }
    return sumField(rollups, 'counts', 'not_yet');
  }, [rollups, isFiltered, selectedSpecialtyId]);

  const urgentCount = useMemo(() => {
    if (isFiltered) {
      const specTotals = getSpecialtyFilteredTotals(rollups, selectedSpecialtyId);
      return specTotals.urgent;
    }
    return sumField(rollups, 'counts', 'urgent');
  }, [rollups, isFiltered, selectedSpecialtyId]);

  const reworkCreated = useMemo(() => {
    if (isFiltered) return totals.reworkCreated;
    return sumField(rollups, 'rework', 'requestsCreated');
  }, [rollups, isFiltered, totals]);

  // Not-Yet Conversion — COHORT metric (C5): of referrals first marked Not-Yet in
  // this window, the share recovered by ENGINE re-evaluation. Sourced from
  // tenants/{t}/rollups/cohorts/notYet/{dateId}. Tenant-level cohort docs only →
  // '—' under specialty filter or multi-tenant aggregate. No legacy fallback.
  const notYetCohortAvailable = !isFiltered && !isMultiTenant && notYetCohort != null;
  const conversionRate = (notYetCohortAvailable && notYetCohort.notYet > 0)
    ? pct(notYetCohort.convertedByEngine, notYetCohort.notYet)
    : null;

  // Override rate (rollup-based: counts.overrides / counts.not_yet)
  const overrideCount = useMemo(() => {
    if (isFiltered) {
      const specTotals = getSpecialtyFilteredTotals(rollups, selectedSpecialtyId);
      return specTotals.overrides;
    }
    return sumField(rollups, 'counts', 'overrides');
  }, [rollups, isFiltered, selectedSpecialtyId]);
  const overrideTotal   = notYetCount;
  const overrideRate    = pct(overrideCount, overrideTotal);

  const reworkResolved = useMemo(() => {
    if (isFiltered) return totals.reworkResolved;
    return sumField(rollups, 'rework', 'resolved');
  }, [rollups, isFiltered, totals]);
  const reworkTouches = useMemo(() => {
    if (isFiltered) return totals.reworkTouches;
    return sumField(rollups, 'rework', 'touchesLogged');
  }, [rollups, isFiltered, totals]);
  // Quality ratios
  const firstPassRate    = pct(totals.firstPassAccepted, totals.gateSubmissions);
  const routineCleanRate = pct(totals.routineClean, totals.gateSubmissions);
  // Cohort metrics (tenant-level only). Unavailable when a specialty filter is
  // active (no per-specialty cohort rollups) or in the multi-tenant aggregate view.
  const cohortAvailable  = !isFiltered && !isMultiTenant && cohort != null;
  const cohortEnteredOps = cohortAvailable ? cohort.enteredOps : null;
  // Closed-Loop Rate = cohort closedLoop / cohort enteredOps over the window.
  const closedLoopRate   = (cohortAvailable && cohort.enteredOps > 0)
    ? pct(cohort.closedLoop, cohort.enteredOps)
    : null;

  // ── Rollup freshness (B6) — read-only; surfaces lag, never changes formulas.
  // lastUpdatedMs = most recent write across the window's daily rollup docs,
  // plus cohort lastComputedAt when cohort metrics are shown. Tenant-level.
  const freshness = useMemo(() => {
    let lastMs = null;
    for (const r of rollups) {
      const ms = toMillis(r?.updatedAt);
      if (ms != null && (lastMs == null || ms > lastMs)) lastMs = ms;
    }
    if (cohortAvailable && cohort?.lastComputedAtMs != null) {
      if (lastMs == null || cohort.lastComputedAtMs > lastMs) lastMs = cohort.lastComputedAtMs;
    }
    if (notYetCohortAvailable && notYetCohort?.lastComputedAtMs != null) {
      if (lastMs == null || notYetCohort.lastComputedAtMs > lastMs) lastMs = notYetCohort.lastComputedAtMs;
    }
    if (lastMs == null) return { state: 'nodata', short: 'No rollup data', text: 'No rollup data for this window.' };
    const age = Date.now() - lastMs;
    if (age < FRESH_RECENT_MS) return { state: 'recent', short: 'Recently updated', text: 'Recently updated — refresh if a just-completed action is missing.' };
    if (age >= FRESH_STALE_MS) return { state: 'stale',  short: `Stale · ${fmtRelativeAge(age)}`, text: `Rollups may be stale — last updated ${fmtRelativeAge(age)}.` };
    return { state: 'normal', short: `Updated ${fmtRelativeAge(age)}`, text: `Rollups updated ${fmtRelativeAge(age)}.` };
  }, [rollups, cohortAvailable, cohort, notYetCohortAvailable, notYetCohort, lastRefresh]);

  // Capacity / value
  const minutesSpent = impact?.assumptions?.minutesSpentPerIncompleteReferral ?? 15;
  const timeReclaimedMin = useMemo(() => {
    if (isFiltered) return totals.reclaimedMinutes || 0;
    return sumField(rollups, 'value', 'reclaimedMinutes');
  }, [rollups, isFiltered, totals]);

  // ── Appointment Exceptions (capacity leakage after scheduling) ────────────
  // Read-only from daily rollups: tenants/{t}/rollups/daily/dates/{dateId}
  //   appointmentExceptions.{patientCancellationCount, clinicCancellationCount,
  //                          rescheduleCount, patientNoShowCount, totalExceptionCount}
  // Missing fields on older rollup docs are treated as 0 by sumField/safeNum.
  //
  // TRUTH RULE: these are EVENT rollups, not appointment cohorts. We display
  // ABSOLUTE COUNTS ONLY — no rates/percentages. A rate would require a future
  // scheduled-appointment cohort so numerator and denominator share one
  // population; the same-window flow.scheduled denominator does NOT, so it is
  // intentionally not used here.
  //
  // Per-specialty breakdown is written by the rollup trigger under
  // breakdowns.bySpecialty.{specialtyId}.appointmentExceptions.*, so counts are
  // available both tenant-wide (sumField) and under a specialty filter
  // (extractSpecialtyBreakdown + safeSpecNum). Missing fields resolve to 0.
  const apptExceptions = useMemo(() => {
    if (isFiltered) {
      const acc = { patientCancellations: 0, clinicCancellations: 0, reschedules: 0, patientNoShows: 0, totalExceptions: 0 };
      for (const rollup of rollups) {
        const sd = extractSpecialtyBreakdown(rollup)[selectedSpecialtyId];
        if (!sd) continue;
        acc.patientCancellations += safeSpecNum(sd, 'appointmentExceptions', 'patientCancellationCount') ?? 0;
        acc.clinicCancellations  += safeSpecNum(sd, 'appointmentExceptions', 'clinicCancellationCount')  ?? 0;
        acc.reschedules          += safeSpecNum(sd, 'appointmentExceptions', 'rescheduleCount')           ?? 0;
        acc.patientNoShows       += safeSpecNum(sd, 'appointmentExceptions', 'patientNoShowCount')        ?? 0;
        acc.totalExceptions      += safeSpecNum(sd, 'appointmentExceptions', 'totalExceptionCount')       ?? 0;
      }
      return acc;
    }
    return {
      patientCancellations: sumField(rollups, 'appointmentExceptions', 'patientCancellationCount'),
      clinicCancellations:  sumField(rollups, 'appointmentExceptions', 'clinicCancellationCount'),
      reschedules:          sumField(rollups, 'appointmentExceptions', 'rescheduleCount'),
      patientNoShows:       sumField(rollups, 'appointmentExceptions', 'patientNoShowCount'),
      totalExceptions:      sumField(rollups, 'appointmentExceptions', 'totalExceptionCount'),
    };
  }, [rollups, isFiltered, selectedSpecialtyId]);

  // Coverage honesty guard (specialty filter only). Older daily rollups predate
  // the per-specialty appointmentExceptions breakdown. For such a doc we cannot
  // attribute its exceptions to a specialty, so a missing per-specialty count is
  // NOT a true 0 — it is missing coverage. Signal: a doc has tenant-wide
  // appointment-exception activity (totalExceptionCount > 0) yet NO specialty
  // anywhere carries an appointmentExceptions breakdown (pre-rollout doc). If any
  // such doc exists in the window, specialty coverage is partial → show '—'.
  const apptExceptionCoveragePartial = useMemo(() => {
    if (!isFiltered) return false;
    for (const rollup of rollups) {
      const tenantWideTotal = sumField([rollup], 'appointmentExceptions', 'totalExceptionCount');
      if (tenantWideTotal > 0) {
        const breakdown = extractSpecialtyBreakdown(rollup);
        const anySpecialtyHasExceptions = Object.values(breakdown).some(
          (sd) => sd && typeof sd === 'object' && sd.appointmentExceptions != null,
        );
        if (!anySpecialtyHasExceptions) return true;
      }
    }
    return false;
  }, [rollups, isFiltered]);

  // When coverage is partial under a specialty filter, suppress counts ('—') to
  // avoid showing a false 0. Counts-only — never a rate.
  const apptExceptionShowDash = isFiltered && apptExceptionCoveragePartial;

  // NOTE: The appointment outcome cohort (appointmentOutcomeCohort.* rollups, the
  // appointmentInstances ledger, and onAppointmentInstanceWrite_updateCohortRollup)
  // remains live as backend infrastructure for future cohort truth. It is
  // intentionally NOT surfaced on this dashboard right now — Appointment Exceptions
  // (event counts above) is the action signal Ops Managers need.

  // ── Loading ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center animate-pulse">
            <Activity className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <div className="h-5 w-56 bg-slate-700/60 rounded animate-pulse mb-1" />
            <div className="h-3 w-36 bg-slate-800/60 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-slate-800/40 h-36 animate-pulse" />
          ))}
        </div>
        <div className="rounded-2xl bg-slate-800/40 h-48 animate-pulse mb-6" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-slate-800/40 h-28 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="rounded-2xl border border-red-500/25 bg-red-950/20 p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-300 mb-2">Failed to Load Dashboard</h3>
          <p className="text-red-400/80 text-sm mb-6">{error}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-800/50 hover:bg-red-700/50 text-red-200 text-sm font-medium transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasData = totals.gateSubmissions > 0 || totals.referralsWritten > 0 || totalStuck > 0;

  // ── Main render ─────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 data-learning-target="occ_header_title" className={`text-xl font-semibold text-white${focusHighlight('occ_header_title', activeFocusTarget)}`}>Operations Command Center</h1>
            <p className="text-sm text-slate-400">
              {isMultiTenant ? 'Aggregated metrics across all tenants' : tenantIdOverride ? 'Platform-wide operational control panel' : 'Operational control panel — one tenant, read-only'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span
            data-learning-target="occ_freshness_indicator"
            title={freshness.text}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border ${
              freshness.state === 'stale'
                ? 'bg-amber-500/10 text-amber-400 border-amber-500/25'
                : 'bg-slate-800/60 text-slate-400 border-slate-700/40'
            }${focusHighlight('occ_freshness_indicator', activeFocusTarget)}`}
          >
            <Clock className="w-3 h-3" />
            {freshness.short}
          </span>
          <div data-learning-target="occ_period_selector" className={`flex items-center gap-1 p-1 rounded-xl bg-slate-800/60${focusHighlight('occ_period_selector', activeFocusTarget)}`}>
            <PeriodBtn label="7d"  active={period === 7}  onClick={() => setPeriod(7)} />
            <PeriodBtn label="30d" active={period === 30} onClick={() => setPeriod(30)} />
            <PeriodBtn label="90d" active={period === 90} onClick={() => setPeriod(90)} />
          </div>
          <select
            data-learning-target="occ_specialty_filter"
            value={selectedSpecialtyId ?? ''}
            onChange={(e) => setSelectedSpecialtyId(e.target.value || null)}
            className={`h-9 px-3 rounded-xl bg-slate-800/60 border border-slate-700/40 text-sm text-slate-200 hover:bg-slate-700/60 transition-all focus:outline-none focus:ring-1 focus:ring-indigo-500/50${focusHighlight('occ_specialty_filter', activeFocusTarget)}`}
          >
            <option value="">All Specialties</option>
            {Object.entries(specialties)
              .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''))
              .map(([id, spec]) => (
                <option key={id} value={id}>{spec.name || id}</option>
              ))}
          </select>
          <button
            onClick={load}
            title={lastRefresh ? `Last refreshed ${lastRefresh.toLocaleTimeString()}` : 'Refresh'}
            className="w-9 h-9 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {headerSlot}

      {/* ── Empty state ── */}
      {!hasData && (
        <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-10 text-center">
          <Activity className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <h3 className="text-base font-medium text-slate-400 mb-1">
            {isFiltered ? 'Insufficient data' : 'No data for this period'}
          </h3>
          <p className="text-sm text-slate-500">
            {isFiltered
              ? `No referral activity for "${specialties[selectedSpecialtyId]?.name || selectedSpecialtyId}" in the last ${period} days.`
              : `No referral activity was recorded in the last ${period} days.`}
          </p>
        </div>
      )}

      {hasData && (
        <>
          {/* ================================================================ */}
          {/* LAYER 1 — LIVE FIRES                                             */}
          {/* ================================================================ */}
          {!hideLiveFires && <div data-learning-target="occ_live_fires_section" className={focusHighlight('occ_live_fires_section', activeFocusTarget).trim()}>
            <SectionHeader
              icon={Flame}
              title="Live Fires"
              subtitle={`${totalStuck} referral${totalStuck !== 1 ? 's' : ''} — operational exceptions that need attention now`}
              color="red"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
              <LiveFireCard
                label="Unclaimed New"
                mainCopy={`${liveFireCards.unclaimedNew.stuckPct}% still waiting for first ownership`}
                helperText="New referrals still waiting for first ownership. Business hours only."
                emptyText="No unclaimed referrals"
                icon={AlertTriangle}
                {...liveFireCards.unclaimedNew}
                learningTarget="occ_fire_unclaimed_new"
                activeFocusTarget={activeFocusTarget}
                onSendPing={canSendQueuePing ? openSendPing(QUEUE_PING_PURPOSE.INTAKE_UNCLAIMED_NEW) : undefined}
              />
              <LiveFireCard
                label="Stuck in Needs Info"
                mainCopy={`${liveFireCards.needsInfo.stuckPct}% waiting too long for required information`}
                helperText="Missing information has not been resolved. Business hours only."
                emptyText="No referrals awaiting information"
                icon={Clock}
                {...liveFireCards.needsInfo}
                learningTarget="occ_fire_stuck_needs_info"
                activeFocusTarget={activeFocusTarget}
                onSendPing={canSendQueuePing ? openSendPing(QUEUE_PING_PURPOSE.INTAKE_NEEDS_INFO) : undefined}
              />

              {/* Triage card — bullet list breakdown */}
              <div data-learning-target="occ_fire_stuck_triage" className={`rounded-2xl border p-5 backdrop-blur-sm flex flex-col gap-3 ${FIRE_COLORS[liveFireCards.triage.status]?.bg ?? FIRE_COLORS.ok.bg} ${FIRE_COLORS[liveFireCards.triage.status]?.border ?? FIRE_COLORS.ok.border}${focusHighlight('occ_fire_stuck_triage', activeFocusTarget)}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Stuck in Triage</span>
                  <div className={`w-7 h-7 rounded-lg ${FIRE_COLORS[liveFireCards.triage.status]?.bg ?? FIRE_COLORS.ok.bg} flex items-center justify-center`}>
                    <ClipboardList className={`w-4 h-4 ${FIRE_COLORS[liveFireCards.triage.status]?.text ?? FIRE_COLORS.ok.text}`} />
                  </div>
                </div>
                {liveFireCards.triage.queueCount === 0 ? (
                  <div className="text-sm text-slate-500">No referrals awaiting triage</div>
                ) : (
                  <>
                    <div className={`text-3xl font-bold ${FIRE_COLORS[liveFireCards.triage.status]?.text ?? FIRE_COLORS.ok.text} leading-none`}>
                      {fmtN(liveFireCards.triage.stuckCount)}
                    </div>
                    <div className={`text-xs ${liveFireCards.triage.status !== 'ok' ? (FIRE_COLORS[liveFireCards.triage.status]?.text ?? '') : 'text-slate-500'}`}>
                      {liveFireCards.triage.stuckPct}% waiting too long for triage review
                    </div>
                    {/* Sub-category bullet list */}
                    <ul className="space-y-1 text-xs">
                      <li className={`flex items-center gap-1.5 ${liveFireCards.triage.urgentStuck > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${liveFireCards.triage.urgentStuck > 0 ? 'bg-red-400' : 'bg-slate-600'}`} />
                        Urgent: {liveFireCards.triage.urgentStuck} stuck / {liveFireCards.triage.urgentCount} total
                        <span className="text-[10px] text-slate-600 ml-auto">SLA {liveFireCards.triage.urgentSlaHours}h</span>
                      </li>
                      <li className={`flex items-center gap-1.5 ${liveFireCards.triage.routineStuck > 0 ? 'text-amber-400' : 'text-slate-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${liveFireCards.triage.routineStuck > 0 ? 'bg-amber-400' : 'bg-slate-600'}`} />
                        Routine: {liveFireCards.triage.routineStuck} stuck / {liveFireCards.triage.routineCount} total
                        <span className="text-[10px] text-slate-600 ml-auto">SLA {liveFireCards.triage.routineSlaHours}h</span>
                      </li>
                      <li className={`flex items-center gap-1.5 ${liveFireCards.triage.holdStuck > 0 ? 'text-purple-400' : 'text-slate-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${liveFireCards.triage.holdStuck > 0 ? 'bg-purple-400' : 'bg-slate-600'}`} />
                        On Hold: {liveFireCards.triage.holdStuck} stuck / {liveFireCards.triage.holdCount} total
                        <span className="text-[10px] text-slate-600 ml-auto">SLA {liveFireCards.triage.holdSlaHours}h</span>
                      </li>
                    </ul>
                  </>
                )}
                <div className="flex items-center gap-2">
                  {liveFireCards.triage.nuclearOverride && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FIRE_COLORS.critical.badge}`}>
                      <Flame className="w-3 h-3 inline mr-1" />Critical — Urgent SLA Breach
                    </span>
                  )}
                  {!liveFireCards.triage.nuclearOverride && liveFireCards.triage.status === 'critical' && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FIRE_COLORS.critical.badge}`}>
                      <Flame className="w-3 h-3 inline mr-1" />Critical — {liveFireCards.triage.stuckPct}% breaching SLA
                    </span>
                  )}
                  {liveFireCards.triage.status === 'warn' && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FIRE_COLORS.warn.badge}`}>
                      <AlertTriangle className="w-3 h-3 inline mr-1" />WARNING
                    </span>
                  )}
                  {liveFireCards.triage.status === 'ok' && liveFireCards.triage.queueCount > 0 && (
                    <span className="text-xs text-slate-500">Within threshold</span>
                  )}
                </div>
                {liveFireCards.triage.queueCount > 0 && (
                  <div className="text-xs text-slate-600">
                    Warn: {liveFireCards.triage.warnThreshold}% | Critical: {liveFireCards.triage.critThreshold}%
                  </div>
                )}
                <div className="text-[10px] text-slate-600">Untriaged referrals in READY_TO_SCHEDULE. Business hours only.</div>
                {canSendQueuePing && liveFireCards.triage.status !== 'ok' && liveFireCards.triage.queueCount > 0 && (
                  <button
                    onClick={openSendPing(QUEUE_PING_PURPOSE.TRIAGE_STUCK)}
                    className="mt-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 text-[11px] font-medium transition-all self-start"
                  >
                    <Megaphone className="w-3.5 h-3.5" /> Send Queue Ping
                  </button>
                )}
              </div>

              <LiveFireCard
                label="Stuck in Scheduling"
                mainCopy={`${liveFireCards.scheduling.stuckPct}% waiting too long to be scheduled`}
                helperText="Clock starts after triage completion. Business hours only."
                emptyText="No referrals awaiting scheduling"
                icon={Timer}
                {...liveFireCards.scheduling}
                learningTarget="occ_fire_stuck_scheduling"
                activeFocusTarget={activeFocusTarget}
                onSendPing={canSendQueuePing ? openSendPing(QUEUE_PING_PURPOSE.SCHEDULING_BACKLOG) : undefined}
              />

              <LiveFireCard
                label="Long-Dated Appointments"
                mainCopy={`${liveFireCards.scheduled.stuckPct}% scheduled too far in the future`}
                helperText="Measured from scheduled date to appointment date. Calendar time."
                emptyText="No scheduled referrals"
                icon={Calendar}
                {...liveFireCards.scheduled}
                learningTarget="occ_fire_long_dated_appointments"
                activeFocusTarget={activeFocusTarget}
                onSendPing={canSendQueuePing ? openSendPing(QUEUE_PING_PURPOSE.LONG_DATED_APPOINTMENTS) : undefined}
              />
              <LiveFireCard
                label="Unreconciled Visits"
                mainCopy={`${fmtN(liveFireCards.unreconciled.stuckCount)} past appointments awaiting an outcome`}
                helperText="Appointment date has passed, but visit outcome is still missing. Ask the responsible team to record Seen, No-Show, or Cancelled so the referral can move forward or close correctly."
                emptyText="No scheduled referrals"
                icon={Timer}
                {...liveFireCards.unreconciled}
                learningTarget="occ_fire_unreconciled_visits"
                activeFocusTarget={activeFocusTarget}
                onSendPing={canSendQueuePing ? openSendPing(QUEUE_PING_PURPOSE.CLOSED_LOOP_UNRECONCILED_VISITS) : undefined}
              />
              <LiveFireCard
                label="Seen, Not Closed"
                mainCopy={`${liveFireCards.seen.stuckPct}% seen but not yet closed loop`}
                helperText="Consult/closure is still pending. Business hours only."
                emptyText="No referrals awaiting closure"
                icon={Eye}
                {...liveFireCards.seen}
                learningTarget="occ_fire_seen_not_closed"
                activeFocusTarget={activeFocusTarget}
                onSendPing={canSendQueuePing ? openSendPing(QUEUE_PING_PURPOSE.CLOSED_LOOP_PENDING) : undefined}
              />
            </div>
            {!liveItems && (
              <p className="text-xs text-slate-600 mt-2">
                Live item data unavailable. Stuck counts will appear once active workflow items can be queried.
              </p>
            )}
          </div>}

          {/* ================================================================ */}
          {/* LAYER 2 — REFERRAL FUNNEL                                        */}
          {/* ================================================================ */}
          <div data-learning-target="occ_referral_funnel_section" className={focusHighlight('occ_referral_funnel_section', activeFocusTarget).trim()}>
            <SectionHeader
              icon={TrendingUp}
              title="Referral Funnel"
              subtitle={`Pipeline flow over the last ${period} days`}
              color="indigo"
            />

            {/* Main funnel strip */}
            <div className="rounded-2xl border border-slate-700/30 bg-slate-900/30 p-5 mb-4">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
                Forward Flow
              </div>
              <div className="flex items-stretch gap-1">
                <FunnelBox label="Gate Submissions" count={totals.gateSubmissions} color="blue" icon={Shield} learningTarget="occ_funnel_gate_submissions" activeFocusTarget={activeFocusTarget} />
                <FunnelArrow />
                <FunnelBox label="Referrals Written" count={totals.referralsWritten} color="teal" icon={FileText} learningTarget="occ_funnel_referrals_written" activeFocusTarget={activeFocusTarget} />
                <FunnelArrow />
                <FunnelBox
                  label="Entered Ops"
                  count={cohortEnteredOps}
                  color="indigo"
                  icon={ClipboardList}
                  learningTarget="occ_funnel_entered_ops"
                  activeFocusTarget={activeFocusTarget}
                  tooltip={isFiltered
                    ? 'Cohort metric unavailable for specialty filter until specialty-level cohort rollups exist.'
                    : 'Referrals that entered active operational work during this window.'}
                />
                <FunnelArrow />
                <FunnelBox label="Scheduled" count={isFiltered ? totals.flowScheduled : sumField(rollups, 'flow', 'scheduled')} color="violet" icon={Timer} learningTarget="occ_funnel_scheduled" activeFocusTarget={activeFocusTarget} />
                <FunnelArrow />
                <FunnelBox
                  label="Closed Loop"
                  count={totals.reachedClosedLoop}
                  color="emerald"
                  icon={CheckCircle2}
                  learningTarget="occ_funnel_closed_loop"
                  activeFocusTarget={activeFocusTarget}
                  tooltip="Referrals closed during this window."
                />
              </div>
              {isFiltered && totals.enteredOps == null && (
                <p className="text-xs text-slate-600 mt-3">
                  Insufficient specialty data for workflow stages — rollups for this specialty may not have accumulated yet.
                </p>
              )}
            </div>

            {/* Appointment Exceptions — capacity leakage after scheduling (secondary) */}
            <div data-learning-target="occ_appointment_exceptions_section" className={`rounded-2xl border border-slate-700/30 bg-slate-900/30 p-5 mt-4${focusHighlight('occ_appointment_exceptions_section', activeFocusTarget)}`}>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-3.5 h-3.5 text-slate-600" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Appointment Exceptions
                </span>
              </div>
              <p className="text-[11px] text-slate-600 mb-1">
                Recorded appointment-stage exception events during the selected window.
              </p>
              <p className="text-[11px] text-slate-600 mb-4">
                Counts show recorded events, not cohort rates.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <DivertedCard
                  label="Patient Cancellations"
                  count={apptExceptions.patientCancellations}
                  display={apptExceptionShowDash ? '—' : undefined}
                  color="red"
                  icon={Ban}
                  learningTarget="occ_appt_patient_cancellations"
                  activeFocusTarget={activeFocusTarget}
                  tooltip="Patient-side appointments cancelled before completion. Shows patient-side leakage after an appointment was already booked."
                />
                <DivertedCard
                  label="Clinic Cancellations"
                  count={apptExceptions.clinicCancellations}
                  display={apptExceptionShowDash ? '—' : undefined}
                  color="amber"
                  icon={AlertTriangle}
                  learningTarget="occ_appt_clinic_cancellations"
                  activeFocusTarget={activeFocusTarget}
                  tooltip="Clinic-side cancellations that returned work back to scheduling. Shows internal capacity, staffing, or clinic-side disruption."
                />
                <DivertedCard
                  label="Reschedules"
                  count={apptExceptions.reschedules}
                  display={apptExceptionShowDash ? '—' : undefined}
                  color="indigo"
                  icon={RotateCcw}
                  learningTarget="occ_appt_reschedules"
                  activeFocusTarget={activeFocusTarget}
                  tooltip="Appointments moved after booking. High reschedule volume can signal scheduling friction, capacity mismatch, or appointment instability."
                />
                <DivertedCard
                  label="Patient No-Shows"
                  count={apptExceptions.patientNoShows}
                  display={apptExceptionShowDash ? '—' : undefined}
                  color="orange"
                  icon={Clock}
                  learningTarget="occ_appt_patient_no_shows"
                  activeFocusTarget={activeFocusTarget}
                  tooltip="Scheduled appointments where the patient did not attend. Shows booked specialist capacity lost at the appointment stage."
                />
                <DivertedCard
                  label="Total Appointment Exceptions"
                  count={apptExceptions.totalExceptions}
                  display={apptExceptionShowDash ? '—' : undefined}
                  color="violet"
                  icon={BarChart3}
                  learningTarget="occ_appt_total_exceptions"
                  activeFocusTarget={activeFocusTarget}
                  tooltip="Total recorded appointment-stage exception events (patient cancellations + clinic cancellations + reschedules + patient no-shows) during the selected window."
                />
              </div>
              {apptExceptionShowDash && (
                <p className="text-[11px] text-slate-600 mt-3">
                  Specialty breakdown available from rollout date forward.
                </p>
              )}
            </div>
          </div>

          {/* ================================================================ */}
          {/* LAYER 3 — SYSTEM QUALITY + VALUE                                 */}
          {/* ================================================================ */}
          <div data-learning-target="occ_system_quality_value_section" className={focusHighlight('occ_system_quality_value_section', activeFocusTarget).trim()}>
            <SectionHeader
              icon={Zap}
              title="System Quality & Value"
              subtitle="Gate quality, rework reality, and capacity impact"
              color="emerald"
            />

            {/* Gate quality row */}
            <div data-learning-target="occ_gate_quality_section" className={`text-xs font-medium text-slate-500 uppercase tracking-wider mb-3${focusHighlight('occ_gate_quality_section', activeFocusTarget)}`}>
              Gate Quality
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <KpiCard
                label="First-Pass Acceptance"
                value={fmtPct(firstPassRate)}
                color="teal"
                icon={Shield}
                learningTarget="occ_gq_first_pass_acceptance"
                activeFocusTarget={activeFocusTarget}
                formula="(Routine + Urgent) / Gate Submissions. Measures how often referrals pass the Gate on first attempt without missing-field rejection."
              />
              <KpiCard
                label="Routine Clean Rate"
                value={fmtPct(routineCleanRate)}
                color="emerald"
                icon={CheckCircle2}
                learningTarget="occ_gq_routine_clean_rate"
                activeFocusTarget={activeFocusTarget}
                formula="Routine / Gate Submissions. The cleanest outcome: no red flags, all required fields present on first attempt."
              />
              <KpiCard
                label="Closed-Loop Rate"
                value={closedLoopRate != null ? fmtPct(closedLoopRate) : '—'}
                color="violet"
                icon={Activity}
                learningTarget="occ_gq_closed_loop_rate"
                activeFocusTarget={activeFocusTarget}
                formula={isFiltered
                  ? 'Cohort metric unavailable for specialty filter until specialty-level cohort rollups exist.'
                  : 'Of referrals that entered ops during this window, the share that reached Closed Loop. Cohort closedLoop / cohort enteredOps. Recent cohorts may still mature.'}
              />
              <KpiCard
                label="Not-Yet Conversion"
                value={fmtPct(conversionRate)}
                color="emerald"
                icon={ArrowRight}
                learningTarget="occ_gq_not_yet_conversion"
                activeFocusTarget={activeFocusTarget}
                formula={(isFiltered || isMultiTenant)
                  ? 'Cohort metric unavailable for specialty filter until specialty-level cohort rollups exist.'
                  : 'Of referrals first marked Not-Yet during this window, the share recovered by engine re-evaluation. Recent cohorts may still mature.'}
              />
              <KpiCard
                label="Override Rate"
                value={fmtPct(overrideRate)}
                color="orange"
                icon={Shield}
                learningTarget="occ_gq_override_rate"
                activeFocusTarget={activeFocusTarget}
                formula={`Not-yet referrals resolved by manual override rather than engine re-evaluation. Overridden / total not-yet referrals (${overrideCount} / ${overrideTotal}).`}
              />
            </div>

            {/* Rework reality row */}
            <div data-learning-target="occ_rework_reality_section" className={`text-xs font-medium text-slate-500 uppercase tracking-wider mb-3${focusHighlight('occ_rework_reality_section', activeFocusTarget)}`}>
              Rework Reality
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <KpiCard
                label="Rework Requests"
                value={reworkCreated != null ? fmtN(reworkCreated) : '—'}
                color="orange"
                icon={RefreshCw}
                learningTarget="occ_rework_requests"
                activeFocusTarget={activeFocusTarget}
                formula="Missing-information requests opened."
              />
              <KpiCard
                label="Rework Touches"
                value={reworkTouches != null ? fmtN(reworkTouches) : '—'}
                color="yellow"
                icon={ClipboardList}
                learningTarget="occ_rework_touches"
                activeFocusTarget={activeFocusTarget}
                formula="Outreach attempts logged to chase missing items."
              />
              <KpiCard
                label="Rework Resolved"
                value={reworkResolved != null ? fmtN(reworkResolved) : '—'}
                color="emerald"
                icon={CheckCircle2}
                learningTarget="occ_rework_resolved"
                activeFocusTarget={activeFocusTarget}
                formula="Missing-information items received or cleared."
              />
            </div>

            {/* Capacity & value row */}
            <div data-learning-target="occ_capacity_created_section" className={`text-xs font-medium text-slate-500 uppercase tracking-wider mb-3${focusHighlight('occ_capacity_created_section', activeFocusTarget)}`}>
              Capacity Created
            </div>
            <div className="grid grid-cols-1 gap-4">
              <KpiCard
                label="Time Reclaimed"
                value={timeReclaimedMin != null ? fmtDuration(timeReclaimedMin) : fmtDuration(notYetCount * minutesSpent)}
                color="emerald"
                icon={Zap}
                learningTarget="occ_capacity_time_reclaimed"
                activeFocusTarget={activeFocusTarget}
                formula={`Specialist time saved by intercepting incomplete referrals before review. Summed from daily rollups (value.reclaimedMinutes), accrued as minutesSavedPerPreventedReferral per prevented referral = ${fmtDuration(timeReclaimedMin)}. Configurable in Impact Settings.`}
              />
            </div>
          </div>
        </>
      )}

      {/* ── Footer ── */}
      <p className="text-xs text-slate-600 text-center pb-2">
        {!hideLiveFires && <>Live Fires from active workflow items + tenant Impact Settings. </>}Funnel &amp; Quality from daily rollups. Zero PHI reads.
        {isFiltered && <> Filtered by specialty — all metrics sourced from per-specialty rollup breakdowns.</>}
        {lastRefresh && <> Last updated {lastRefresh.toLocaleTimeString()}.</>}
      </p>

      {queuePingPurpose && (
        <CreateQueuePingModal
          purpose={queuePingPurpose}
          source="LIVE_FIRES"
          onClose={() => setQueuePingPurpose(null)}
        />
      )}
    </div>
  );
}
